import { Component } from "react";
import { Button } from "./button";
import { StatusMessage } from "./status-message";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const MAX_RETRIES = 3;
function readStoredRetries(key) {
  if (!key) return 0;
  try {
    const count = Number(sessionStorage.getItem(key));
    return Number.isInteger(count) && count > 0 ? count : 0;
  } catch {
    return 0;
  }
}
function writeStoredRetries(key, count) {
  if (!key) return;
  try {
    if (count > 0) sessionStorage.setItem(key, String(count));else sessionStorage.removeItem(key);
  } catch {
    // Storage can be unavailable (private mode); the in-memory count still applies.
  }
}
export class ErrorBoundary extends Component {
  state = {
    error: null,
    retryCount: readStoredRetries(this.props.persistKey)
  };
  static getDerivedStateFromError(error) {
    return {
      error
    };
  }
  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught an error:", error, info.componentStack);
    // An error ends the healthy streak, whether or not a retry follows.
    clearTimeout(this.healthyTimer);
    this.scheduleRetry();
  }
  componentDidMount() {
    this.watchHealth();
  }
  componentDidUpdate(_prevProps, prev) {
    if (prev.error && !this.state.error) this.watchHealth();
  }
  componentWillUnmount() {
    clearTimeout(this.retryTimer);
    clearTimeout(this.healthyTimer);
  }

  // Once the children have stayed error-free for a while, the earlier retries
  // are history and the next incident starts with a full budget.
  watchHealth() {
    clearTimeout(this.healthyTimer);
    if (this.state.error) return;
    const {
      resetAfter = 30_000,
      persistKey
    } = this.props;
    this.healthyTimer = setTimeout(() => {
      // Never grant a fresh budget while an error is showing: the message
      // would flip to "retrying" with no retry scheduled.
      if (this.state.error) return;
      writeStoredRetries(persistKey, 0);
      if (this.state.retryCount > 0) this.setState({
        retryCount: 0
      });
    }, resetAfter);
  }

  // Automatic retries only make sense when the caller says how to reset.
  scheduleRetry() {
    const {
      onReset,
      retryInterval = 5000,
      persistKey
    } = this.props;
    if (!onReset || this.state.retryCount >= MAX_RETRIES) return;
    clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => {
      const retryCount = this.state.retryCount + 1;
      // Persist before resetting: a reload would otherwise lose the count.
      writeStoredRetries(persistKey, retryCount);
      onReset();
      this.setState({
        error: null,
        retryCount
      });
    }, retryInterval);
  }
  handleManualRetry = () => {
    writeStoredRetries(this.props.persistKey, 0);
    this.props.onReset?.();
    this.setState({
      error: null,
      retryCount: 0
    });
  };
  render() {
    const {
      error,
      retryCount
    } = this.state;
    const {
      children,
      fallback,
      onReset
    } = this.props;
    if (!error) return children;
    if (fallback) return fallback;
    const willRetry = !!onReset && retryCount < MAX_RETRIES;
    return /*#__PURE__*/_jsx("div", {
      className: "flex min-h-screen flex-col items-center justify-center p-4",
      children: /*#__PURE__*/_jsxs("div", {
        className: "w-full max-w-md space-y-4",
        children: [/*#__PURE__*/_jsx(StatusMessage, {
          message: willRetry ? "Kunde inte ansluta till servern. Försöker igen om några sekunder..." : "Kunde inte ansluta till servern. Vänligen försök igen senare.",
          variant: "error"
        }), onReset && !willRetry && /*#__PURE__*/_jsx(Button, {
          onClick: this.handleManualRetry,
          className: "w-full",
          children: "F\xF6rs\xF6k igen"
        })]
      })
    });
  }
}